/**
 * On-demand memory diagnostics for before/after comparisons of memory
 * optimizations (initially: the object tree search result cap).
 *
 * Two parts:
 *
 *  1. A heap measurement based on `performance.measureUserAgentSpecificMemory()`.
 *     This app runs cross-origin isolated (COEP `require-corp`, needed for
 *     `SharedArrayBuffer`), which is exactly the context where that API is
 *     available. It waits for GC internally, so readings are comparable across
 *     runs — unlike the legacy `performance.memory`, which is kept only as a
 *     fallback.
 *
 *  2. A registry where features report byte-attributable stats about their own
 *     large structures (e.g. search result counts, tree node counts). Heap
 *     totals tell you *that* memory grew; these stats tell you *what* grew.
 *
 * Usage from the browser console:
 *
 *   await window.__NOVO_MEMORY__()
 *
 * Returns `{ heap, stats }` and logs it. Run once before an interaction and
 * once after, and diff the two.
 */

export type MemoryStats = Record<string, unknown>;

export interface MemoryDiagnosticsReport {
    /** ISO timestamp of the reading, to keep manual before/after notes honest. */
    timestamp: string;
    heap: {
        /** Bytes reported by the browser, or undefined when no API is available. */
        bytes?: number;
        /** Which API produced the number. */
        source: "measureUserAgentSpecificMemory" | "performance.memory" | "unavailable";
        /** Per-type breakdown when measureUserAgentSpecificMemory provides one. */
        breakdown?: unknown;
    };
    /** One entry per registered provider, keyed by provider name. */
    stats: Record<string, MemoryStats>;
}

const providers = new Map<string, () => MemoryStats>();

/**
 * Register a named stats provider. Returns an unregister function; callers
 * mounted in React should register in an effect and unregister on cleanup.
 * Registering the same name again replaces the previous provider (e.g. the
 * object tree window and side panel taking turns).
 */
export function registerMemoryStatsProvider(name: string, provider: () => MemoryStats): () => void {
    providers.set(name, provider);
    return () => {
        // Only remove if it is still our provider — a later registration under
        // the same name must not be torn down by a stale cleanup.
        if (providers.get(name) === provider) {
            providers.delete(name);
        }
    };
}

interface MemoryMeasurement {
    bytes: number;
    breakdown?: unknown;
}

async function measureHeap(): Promise<MemoryDiagnosticsReport["heap"]> {
    const perf = globalThis.performance as
        | (Performance & {
              measureUserAgentSpecificMemory?: () => Promise<MemoryMeasurement>;
              memory?: { usedJSHeapSize?: number };
          })
        | undefined;

    if (perf?.measureUserAgentSpecificMemory && globalThis.crossOriginIsolated) {
        try {
            const result = await perf.measureUserAgentSpecificMemory();
            return { bytes: result.bytes, breakdown: result.breakdown, source: "measureUserAgentSpecificMemory" };
        } catch {
            // Fall through to the legacy API (e.g. measurement aborted by navigation).
        }
    }

    const used = perf?.memory?.usedJSHeapSize;
    if (typeof used === "number") {
        return { bytes: used, source: "performance.memory" };
    }

    return { source: "unavailable" };
}

/**
 * Snapshot registered provider stats. Does not wait for GC — use
 * {@link collectMemoryDiagnostics} when you need a heap reading.
 */
export function collectMemoryStats(): Record<string, MemoryStats> {
    const stats: Record<string, MemoryStats> = {};
    for (const [name, provider] of providers) {
        try {
            stats[name] = provider();
        } catch (ex) {
            stats[name] = { error: String(ex) };
        }
    }
    return stats;
}

/**
 * Take a full reading: heap plus every registered provider's stats.
 * Provider errors are reported in place of their stats rather than failing
 * the whole reading.
 */
export async function collectMemoryDiagnostics(): Promise<MemoryDiagnosticsReport> {
    return {
        timestamp: new Date().toISOString(),
        heap: await measureHeap(),
        stats: collectMemoryStats(),
    };
}

declare global {
    interface Window {
        /** Console entry point for memory diagnostics — see module doc. */
        __NOVO_MEMORY__?: () => Promise<MemoryDiagnosticsReport>;
    }
}

if (typeof window !== "undefined") {
    window.__NOVO_MEMORY__ = async () => {
        const report = await collectMemoryDiagnostics();
        // eslint-disable-next-line no-console
        console.log("[memory]", report);
        return report;
    };
}
